import React from "react";

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Modal,
  Popover,
  ScrollArea,
  Switch,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";

import { useEffect, useState } from "react";
import useStyles from "./style";

import { useSelector } from "react-redux";
import UserDetail from "../MarTab/UserDetail";
import CustomPagination from "../../../../components/Pagination/CustomPagination";
import TableLoader from "../../../../components/TableLoader";
import NotFound from "../NotFound";
import {
  IconDownload,
  IconEdit,
  IconFile,
  IconTrash,
  IconFileTypePdf,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { decrypt, formatDate, formatDateWithMedTimezone } from "../../../../utils";
import HideClient from "../ClientHide";
import { IconEye } from "@tabler/icons-react";
import ClientColumn from "../ClientColumn";
import DiscontinueModal from "../../../../components/DiscontinueModal";
import { useDisclosure } from "@mantine/hooks";
import { deleteMedication } from "../../../../services/patient";
import FillMedicationModal from "../../../../components/FillMedicationModal";
import { getMar } from "../../../../services";
import { isStaff } from "../../../../http";
import MedicationListPDF from "../../../../components/MedicationListPDF";
import NarcoticsReportPDF from "../../../../components/NarcoticsReportPDF";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import moment from "moment";
import { https } from "../../../../http";

const MedicationListTab = ({
  userId,
  clientId,
  clientDetails,
  onEditClick,
}) => {
  const { classes } = useStyles();
  const [scrolled, setScrolled] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowsCopy, setRowsCopy] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [show, setShow] = useState(true);
  const [selectDiscontinue, setSelectDiscontinue] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [narcoticPdfLoading, setNarcoticPdfLoading] = useState(false);
  const [narcMonthPickerOpen, setNarcMonthPickerOpen] = useState(false);
  const [narcSelectedMonth, setNarcSelectedMonth] = useState(new Date());

  const { selectedClient } = useSelector((state) => state.mar);
  const { allPatients } = useSelector((state) => state.patient);
  const { loading } = useSelector((state) => state.patient);
  const { profile } = useSelector((state) => state?.profile);
  const allFacilities = useSelector((state) => state?.facility);
  const [activeMedicines, setActiveMedicines] = useState([]);

  const [isActive, setIsActive] = useState(true);

  let inActiveMedicinesData = Array.isArray(rows)
    ? rows?.filter((item) => item?.discontinued === true)
    : [];

  let activeMedicinesData = Array.isArray(rows)
    ? rows.filter((item) => item?.discontinued !== true)
    : [];

  const activeInActiveMedicines = (bool) => {
    if (bool) {
      setIsActive(true);
      setActiveMedicines(activeMedicinesData);
      setCurrentPage(1);
    } else {
      setIsActive(false);
      setActiveMedicines(inActiveMedicinesData);
      setCurrentPage(1);
    }
    setActiveMedicines(isActive ? activeMedicinesData : inActiveMedicinesData);
  };

  useEffect(() => {
    if (rows && rows?.length > 0) {
      activeInActiveMedicines(isActive);
    }
  }, [rows, isActive]);

  useEffect(() => {
    const filteredData = isActive ? activeMedicinesData : inActiveMedicinesData;
    setActiveMedicines(
      filteredData.slice((currentPage - 1) * 10, currentPage * 10)
    );
  }, [currentPage, isActive, rowsCopy]);

  useEffect(() => {
    if (selectedClient?.form3?.length > 0) {
      setRows(selectedClient.form3);
      setRowsCopy(selectedClient.form3);
    } else {
      setRows([]);
      setRowsCopy([]);
    }
  }, [selectedClient]);

  /* ── PDF handler ── */
  const handleMedListPdf = async () => {
    if (!rows?.length) return;
    setPdfLoading(true);
    try {
      const facilityName = profile?.business?.companyName ||
        selectedClient?.facility?.name ||
        (Array.isArray(allFacilities?.facilities) ? allFacilities.facilities.find((f) => f.id === selectedClient?.facilityId)?.name : null) ||
        profile?.facility?.name || "Facility";
      const facilityPhoto = decrypt(
        selectedClient?.facility?.imageurl ||
        (Array.isArray(allFacilities?.facilities) ? allFacilities.facilities.find((f) => f.id === selectedClient?.facilityId)?.imageurl : null) ||
        profile?.facility?.imageurl
      );
      const rawName = selectedClient?.name || "";
      const clientName = rawName ? (decrypt(rawName) || rawName) : "Client";
      const blob = await pdf(
        <MedicationListPDF
          clientData={{ ...selectedClient, name: clientName }}
          medications={rows}
          facilityName={facilityName}
          facilityPhoto={facilityPhoto}
        />
      ).toBlob();
      saveAs(blob, `Medication_List_${clientName.replace(/\s+/g, "_")}_${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
    }
    setPdfLoading(false);
  };

  const handleNarcoticsPdfClick = () => {
    setNarcSelectedMonth(new Date());
    setNarcMonthPickerOpen(true);
  };

  const handleNarcoticsPdfGenerate = async () => {
    setNarcMonthPickerOpen(false);
    setNarcoticPdfLoading(true);
    try {
      const reportMonth = moment(narcSelectedMonth).month() + 1;
      const reportYear = moment(narcSelectedMonth).year();
      const monthStart = moment(narcSelectedMonth).startOf("month");
      const monthEnd = moment(narcSelectedMonth).endOf("month");

      // Include narcotics that were active during the selected month:
      // - Currently active narcotics
      // - Narcotics discontinued DURING or AFTER the selected month
      const narcotics = rows?.filter((r) => {
        if (r?.isNarcotic !== "Yes" && r?.isNarcotic !== true) return false;
        if (!r?.discontinued) return true; // active — include
        // Discontinued — include if discontinued during or after this month
        const discDate = r?.discontinuedDate || r?.discontinuedAt || r?.updatedAt;
        if (!discDate) return false;
        return moment(discDate).isSameOrAfter(monthStart, "month");
      });

      if (!narcotics?.length) {
        setNarcoticPdfLoading(false);
        return;
      }

      const facilityName = profile?.business?.companyName ||
        selectedClient?.facility?.name ||
        (Array.isArray(allFacilities?.facilities) ? allFacilities.facilities.find((f) => f.id === selectedClient?.facilityId)?.name : null) ||
        profile?.facility?.name || "Facility";
      const facilityPhoto = decrypt(
        selectedClient?.facility?.imageurl ||
        (Array.isArray(allFacilities?.facilities) ? allFacilities.facilities.find((f) => f.id === selectedClient?.facilityId)?.imageurl : null) ||
        profile?.facility?.imageurl
      );
      const rawName2 = selectedClient?.name || "";
      const clientName = rawName2 ? (decrypt(rawName2) || rawName2) : "Client";

      // Fetch MAR records for this client for the selected month
      let marRecords = [];
      try {
        const clientIdForMar = selectedClient?.id || selectedClient?._id;
        const res = await https().get(
          `/mar/get?client=${clientIdForMar}&page=all&filterDateStart=${monthStart.toISOString()}&filterDateEnd=${monthEnd.toISOString()}`
        );
        marRecords = res?.data?.data?.newMar || [];
      } catch (marErr) {
        console.warn("Could not fetch MAR records for narcotics report:", marErr);
      }

      const blob = await pdf(
        <NarcoticsReportPDF
          clientData={{ ...selectedClient, name: clientName }}
          narcoticMeds={narcotics}
          marRecords={marRecords}
          facilityName={facilityName}
          facilityPhoto={facilityPhoto}
          reportMonth={reportMonth}
          reportYear={reportYear}
        />
      ).toBlob();
      const monthLabel = moment(narcSelectedMonth).format("MMMM_YYYY");
      saveAs(blob, `Narcotics_Log_${clientName.replace(/\s+/g, "_")}_${monthLabel}.pdf`);
    } catch (err) {
      console.error("Narcotics PDF generation error:", err);
    }
    setNarcoticPdfLoading(false);
  };

  /* ── Counts for header bar ── */
  const totalMeds = rows?.length || 0;
  const activeCount = activeMedicinesData?.length || 0;
  const inactiveCount = inActiveMedicinesData?.length || 0;
  const narcoticCount = rows?.filter((r) => r?.isNarcotic === "Yes" || r?.isNarcotic === true)?.length || 0;

  return (
    <div className={classes.content}>
      {/* ── Top toolbar ── */}
      <Box px={20} pt={14} pb={10}>
        {/* Row 1: Toggle + Export buttons */}
        <Flex justify="space-between" align="center" mb={12}>
          <Flex align="center" gap={8}>
            <Box className={classes.activeInactiveParent} style={{ margin: 0 }}>
              <Button
                variant="danger"
                className={`${classes.btnCommonCSS} ${classes.activeButton} ${
                  isActive && classes.activeBg
                }`}
                onClick={() => activeInActiveMedicines(true)}
              >
                <p>Active</p>
              </Button>
              <Button
                id="medication-today"
                variant="danger"
                className={`${classes.btnCommonCSS} ${classes.inActiveButton} ${
                  !isActive && classes.activeBg
                }`}
                onClick={() => activeInActiveMedicines(false)}
              >
                <p>Inactive</p>
              </Button>
            </Box>
          </Flex>

          <Group spacing={8}>
            <Button
              variant="outline"
              size="xs"
              leftIcon={pdfLoading ? <Loader size={14} color="dark" /> : <IconFileTypePdf size={16} />}
              onClick={handleMedListPdf}
              disabled={pdfLoading || !rows?.length}
              styles={{
                root: {
                  borderColor: "#1D2939",
                  color: "#1D2939",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "#f8f8f6" },
                },
              }}
            >
              Medication List
            </Button>
            <Button
              variant="outline"
              size="xs"
              leftIcon={narcoticPdfLoading ? <Loader size={14} color="red" /> : <IconAlertTriangle size={16} />}
              onClick={handleNarcoticsPdfClick}
              disabled={narcoticPdfLoading || narcoticCount === 0}
              styles={{
                root: {
                  borderColor: "#C62828",
                  color: "#C62828",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "#FFF5F5" },
                },
              }}
            >
              Narcotics Log
            </Button>
          </Group>
        </Flex>

        {/* Row 2: Summary stats bar */}
        <Flex
          gap={16}
          align="center"
          py={8}
          px={14}
          sx={{
            backgroundColor: "#F8F9FA",
            borderRadius: 8,
            border: "1px solid #E9ECEF",
          }}
        >
          <Flex align="center" gap={6}>
            <Text size="xs" color="dimmed" weight={500}>Total</Text>
            <Badge size="sm" variant="filled" color="dark" radius="sm">{totalMeds}</Badge>
          </Flex>
          <Box sx={{ width: 1, height: 20, backgroundColor: "#DEE2E6" }} />
          <Flex align="center" gap={6}>
            <Box sx={{ width: 8, height: 8, borderRadius: 4, backgroundColor: "#2E7D32" }} />
            <Text size="xs" color="dimmed" weight={500}>Active</Text>
            <Badge size="sm" variant="light" color="green" radius="sm">{activeCount}</Badge>
          </Flex>
          <Flex align="center" gap={6}>
            <Box sx={{ width: 8, height: 8, borderRadius: 4, backgroundColor: "#9E9E9E" }} />
            <Text size="xs" color="dimmed" weight={500}>Inactive</Text>
            <Badge size="sm" variant="light" color="gray" radius="sm">{inactiveCount}</Badge>
          </Flex>
          {narcoticCount > 0 && (
            <Flex align="center" gap={6}>
              <Box sx={{ width: 8, height: 8, borderRadius: 4, backgroundColor: "#C62828" }} />
              <Text size="xs" color="dimmed" weight={500}>Narcotics</Text>
              <Badge size="sm" variant="light" color="red" radius="sm">{narcoticCount}</Badge>
            </Flex>
          )}
        </Flex>
      </Box>

      {!userId ? (
        <HideClient show={show} setShow={setShow} />
      ) : (
        <Box m={20}></Box>
      )}
      <div className={classes.scheduleDetail}>
        {show ? (
          <>{!userId && <UserDetail isClient={false} />}</>
        ) : !loading ? (
          <ClientColumn />
        ) : (
          <></>
        )}

        <div style={{ width: userId ? "100%" : "100%", overflowX: "auto" }}>
          <div className={classes.wrapper}>
            {loading ? (
              <Box
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "100px 0 0 0",
                }}
              >
                <Loader color="yellow" size={"md"} />
              </Box>
            ) : (
              <div className={classes.mainContainer}>
                <ScrollArea
                  onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
                  sx={{ minHeight: "350px" }}
                >
                  <Table
                    withColumnBorders
                    withBorder
                    highlightOnHover
                    enablestickyheader
                    sx={{ fontFamily: "Inter", minWidth: "2000px" }}
                    fontSize={"sm"}
                    verticalSpacing={"md"}
                  >
                    <TableHeader scrolled={scrolled} />
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={1000}>
                            <TableLoader />
                          </td>
                        </tr>
                      ) : activeMedicines?.length > 0 ? (
                        <TableRow
                          data={activeMedicines}
                          open={open}
                          clientId={clientId}
                          onEditClick={onEditClick}
                          setSelectDiscontinue={setSelectDiscontinue}
                        />
                      ) : (
                        <NotFound />
                      )}
                    </tbody>
                  </Table>
                </ScrollArea>
                <Flex justify={"flex-end"}>
                  <CustomPagination
                    data={
                      isActive
                        ? activeMedicinesData?.length
                        : inActiveMedicinesData?.length
                    }
                    itemsPerPage={10}
                    currentPage={currentPage}
                    handlePageChange={(page) => {
                      setCurrentPage(page);
                    }}
                  />
                </Flex>
                <DiscontinueModal
                  opened={opened}
                  onclose={() => close()}
                  data={selectDiscontinue}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Narcotic Report Month Picker Modal */}
      <Modal
        opened={narcMonthPickerOpen}
        onClose={() => setNarcMonthPickerOpen(false)}
        title="Generate Narcotics Log"
        centered
        size="sm"
        styles={{
          title: { fontWeight: 700, fontFamily: "Inter" },
          body: { overflow: "visible" },
          content: { overflow: "visible" },
        }}
        overlayProps={{ zIndex: 1000 }}
        transitionProps={{ duration: 200 }}
        radius="md"
        zIndex={1000}

      >
        <Text size="sm" color="dimmed" mb={16}>
          Select the month for the Controlled Substance &amp; Narcotic Monthly Count report.
        </Text>
        <MonthPickerInput
          label="Report Month"
          placeholder="Select month"
          value={narcSelectedMonth}
          onChange={setNarcSelectedMonth}
          maxDate={new Date()}
          mb={20}
          popoverProps={{ withinPortal: true, zIndex: 1000 }}
          styles={{
            input: { fontFamily: "Inter" },
            label: { fontWeight: 600, fontFamily: "Inter" },
          }}
        />
        <Flex justify="flex-end" gap={8}>
          <Button
            variant="outline"
            color="gray"
            onClick={() => setNarcMonthPickerOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="red"
            leftIcon={<IconAlertTriangle size={16} />}
            onClick={handleNarcoticsPdfGenerate}
            loading={narcoticPdfLoading}
          >
            Generate Report
          </Button>
        </Flex>
      </Modal>
    </div>
  );
};

const TableHeader = ({ scrolled }) => {
  const { classes, cx } = useStyles();
  const { profile } = useSelector((state) => state?.profile);
  return (
    <thead
      className={cx(classes.header, {
        [classes.scrolled]: scrolled,
      })}
    >
      <tr>
        <th className={classes.stickyColumn} style={{ minWidth: "80px" }}>
          Medication Name
        </th>
        <th style={{ minWidth: "80px" }}>Prescription</th>
        <th style={{ minWidth: "80px" }}>Dosage Amount</th>
        <th style={{ minWidth: "80px" }}>Dosage Unit</th>
        <th style={{ minWidth: "80px" }}>Frequency</th>
        <th style={{ minWidth: "80px" }}>Medication Type</th>
        <th style={{ minWidth: "80px" }}>Start Date</th>
        <th style={{ minWidth: "80px" }}>Expiration Date</th>
        <th style={{ minWidth: "80px" }}>Narcotic</th>
        <th style={{ minWidth: "80px" }}>Medication Route</th>
        <th style={{ minWidth: "80px" }}>Dosage Form</th>
        <th style={{ minWidth: "80px" }}>Current Count</th>
        <th style={{ minWidth: "80px" }}>Total Quantity</th>
        <th style={{ minWidth: "80px" }}>Discontinued Date</th>
        {!isStaff(profile?.user?.role) ? (
          <th style={{ minWidth: "80px" }}>Active</th>
        ) : null}
        <th style={{ minWidth: "80px" }}>Actions</th>
      </tr>
    </thead>
  );
};

const TableRow = ({
  data,
  onEditClick,
  setSelectDiscontinue,
  open,
  clientId,
}) => {
  const { classes } = useStyles();
  const { profile } = useSelector((state) => {
    return state?.profile;
  });

  const downloadFile = async (url) => {
    try {
      if (url) {
        const response = await fetch(url);
        const blob = await response.blob();
        const filename = url?.split("/")?.pop();
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      } else {
        console.error("No url found");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const DeleteComponent = ({ id, name, onPressOk }) => {
    const { addPatientLoading } = useSelector((state) => state?.patient);
    const [deleteModal, setDeleteModal] = useState(false);

    return (
      <Popover
        opened={deleteModal}
        width={300}
        position="top"
        withArrow
        shadow="md"
      >
        <Popover.Target>
          <ActionIcon
            color="red"
            variant="light"
            onClick={() => setDeleteModal(true)}
            title="Delete"
          >
            <IconTrash id="icon-trash" />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Text size="sm">
            This action is permanent. Deleting medications cannot be undone.
            Are you sure you want to delete this medication?
          </Text>

          <Group grow mt={10}>
            <Button
              onClick={() => setDeleteModal(false)}
              size={"xs"}
              color="yellow"
            >
              Cancel
            </Button>
            <Button
              id="confirm-delete"
              loading={addPatientLoading}
              size={"xs"}
              color={"red"}
              onClick={() => onPressOk(id, name)}
            >
              Delete
            </Button>
          </Group>
        </Popover.Dropdown>
      </Popover>
    );
  };

  const remove = async (medicationId, name) => {
    await deleteMedication({
      clientId,
      medicationId,
      medicationName: name,
    });

    await getMar(localStorage.getItem("calendar_date"), clientId);
  };

  return (
    <>
      {data?.map((row, i) => {
        const bg = i % 2 === 0;

        const isDiscontinued = "discontinued" in row;
        const isNarcotic = row?.isNarcotic === "Yes" || row?.isNarcotic === true;

        return (
          <tr
            key={i}
            className={
              isDiscontinued ? classes.bgDis : bg ? classes.bg2 : classes.bg1
            }
          >
            <td className={classes.stickyColumn}>
              <Flex align="center" gap={6}>
                {row?.medicationName || "-"}
                {isNarcotic && (
                  <Badge size="xs" color="red" variant="light" radius="sm">
                    Narcotic
                  </Badge>
                )}
              </Flex>
            </td>
            <td style={{ color: row?.isPrescribed ? "#F00" : "black" }}>
              {row?.isPrescribed || "No"}
            </td>
            <td>{row?.dosage || "-"}</td>
            <td>{row?.unit || "-"}</td>
            <td>{row?.frequency || "-"}</td>
            <td>{row?.medicationType || "-"}</td>
            <td>
              {row?.medicationDate ? formatDate(row?.medicationDate) : "-"}
            </td>
            <td>
              {row?.expirationDate === "null" || (row?.hasExpiryDate == "false" || row?.hasExpiryDate == false)
                ? "-"
                : formatDate(row?.expirationDate)}
            </td>
            <td style={{ color: isNarcotic ? "#C62828" : "black", fontWeight: isNarcotic ? 600 : 400 }}>
              {row?.isNarcotic || "No"}
            </td>
            <td>{row?.ingestionMode || "-"}</td>
            <td>{row?.dosageForm || "-"}</td>
            <td style={{ textTransform: "capitalize" }}>
              {row?.medicineRemaining || "0"}
            </td>
            <td style={{ textTransform: "capitalize" }}>
              {row?.totalMedicine || "-"}
            </td>

            <td>
              {row?.discontinuedOn ? formatDate(row?.discontinuedOn) : "-"}
            </td>

            {!isStaff(profile?.user?.role) ? (
              <td>
                <Switch
                  size="md"
                  color="yellow"
                  disabled={isDiscontinued ? row?.discontinued : false}
                  checked={isDiscontinued ? !row?.discontinued : true}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      setSelectDiscontinue(row);
                      open();
                    }
                  }}
                />
              </td>
            ) : null}

            <td>
              <div style={{ display: "flex", gap: "10px" }}>
                <ActionIcon
                  id="preview"
                  color="yellow"
                  variant="light"
                  onClick={() => {
                    onEditClick(row, true);
                  }}
                  title="Preview"
                >
                  <IconEye />
                </ActionIcon>
                {!isDiscontinued && !isStaff(profile?.user?.role) && (
                  <ActionIcon
                    id="edit"
                    color="yellow"
                    variant="light"
                    onClick={(e) => {
                      onEditClick(row, false);
                    }}
                    title="Edit"
                  >
                    <IconEdit />
                  </ActionIcon>
                )}
                {!isDiscontinued &&
                  !isStaff(profile?.user?.role) &&
                  !row?.discontinueNote &&
                  row?.medicineRemaining !== "unlimited" &&
                  row?.totalMedicine !== "unlimited" && (
                    <FillMedicationModal data={row} clientId={clientId} />
                  )}
                {isDiscontinued && row?.discontinueNote && (
                  <ActionIcon
                    color="yellow"
                    variant="light"
                    onClick={(e) => {
                      downloadFile(row?.discontinueNote);
                    }}
                    title="Download File"
                  >
                    <IconFile />
                  </ActionIcon>
                )}
                {row?.doctorNote && !isStaff(profile?.user?.role) && (
                  <ActionIcon
                    color="yellow"
                    variant="light"
                    onClick={(e) => {
                      downloadFile(row?.doctorNote);
                    }}
                    title="Doctor's Note"
                  >
                    <IconDownload />
                  </ActionIcon>
                )}
                {!isStaff(profile?.user?.role) ? (
                  <DeleteComponent
                    id={row?.id}
                    name={row?.medicationName}
                    onPressOk={remove}
                  />
                ) : null}
              </div>
            </td>
          </tr>
        );
      })}
    </>
  );
};

export default MedicationListTab;
